import { Col, Form, Input, Row } from "antd"
import { getLocalStorageBrowserLocation } from "helpers/location"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import { debounce } from "lodash"
import GooglePlacesNewInput from "modules/google/GooglePlacesNewInput"
import useGeocoder from "modules/google/hooks/useGeocoder"
import usePredictions from "modules/google/hooks/usePredictions"
import { serialize } from "object-to-formdata"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { tripLatLngSelector, updateTrip } from "store/trips"
import { CustomForm } from "ui/Form"
import { notification } from "ui/Notification"
import { LocationCard } from "./LocationCard"
import { LocationInfo } from "./LocationForm"
import TripLocation from "./utils/Location"

export const ListingLocation = () => {
  const { TextArea } = Input

  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const { id } = useParams()
  const { t } = useTranslation()
  const tripLatLng = useSelector(tripLatLngSelector)
  const geocoder = useGeocoder(null)
  const trip = useSelector(modelSelector("trip"))
  const browserLocation = useMemo(() => getLocalStorageBrowserLocation(), [])
  const [centerLatLng, setCenterLatLng] = useState(tripLatLng || browserLocation)
  const [meetingLatLng, setMeetingLatLng] = useState(null)
  const [manualAddress, setManualAddress] = useState(false)
  const [isEditing, setIsEditing] = useState(false) // Track if "Edit" mode is active
  const [selectPlace] = usePredictions()

  useEffect(() => {
    if (trip?.address) {
      const tripLatLng = { lat: trip.latitude, lng: trip.longitude }
      setCenterLatLng(tripLatLng)
      setMeetingLatLng(trip?.address)
    }
  }, [trip])  

  useEffect(() => {
    if (!meetingLatLng) {
      form.setFieldsValue({ address: "" }) // Clear the address field if meetingLatLng is null
    }
  }, [meetingLatLng, form])

  // Define the changeHandler function
  const changeHandler = ({ target: { name, value } }) => {
    form.setFieldsValue({ [name]: value }) // Update the form field value
  }

  // eslint-disable-next-line
  const debounceCoordinatesToAddress = useCallback(
    debounce(
      (location) =>
        geocoder({ location }, (place) => {
          changeHandler({ target: { name: "address", value: place.formatted_address } })
        }),
      200
    ),
    []
  )
  const placeChangedHandler = useCallback(({ target: { coordinates, value } }) => {
    setIsEditing(false)
    if (coordinates) debounceCoordinatesToAddress(coordinates)
    setCenterLatLng(coordinates)
    if (value) {
      setMeetingLatLng(value)
    }
  }, []) // eslint-disable-line

  const handleEdit = () => {
    setIsEditing(true) // Enable "Edit" mode
  }

  const onFinish = (callback) => {
    form.validateFields().then(() => {
      const formValues = form.getFieldsValue() // Renamed to formValues
      const { special_instructions } = formValues
      // Check if the address is missing
      if (!meetingLatLng) {
        notification.error({ message: "Please confirm the address first." })
        return // Stop the submission
      }
      const formData = serialize(
        {
          trip: {
            special_instructions: special_instructions || "",
            latitude: centerLatLng?.lat,
            longitude: centerLatLng?.lng,
            address: meetingLatLng
          }
        },
        { allowEmptyArrays: true }
      )
      dispatch(updateTrip(id, formData)).then(callback)
    })
  }

  return (
    <CustomForm form={form} layout="vertical" onFinish={onFinish}>
      <Row gutter={[16, 16]} className="mb-3xl">
        <Col xs={24} sm={24} md={12}>
          {(meetingLatLng && !isEditing) ||
            (!manualAddress && (
              <Form.Item name="address" rules={[{ required: true, message: t("listings.tabs.location.validation.address_validate") }]}>
                <GooglePlacesNewInput
                  placeholder="Enter your address"
                  bsSize="sm" // Changed to 'lg' for better visibility
                  onChange={placeChangedHandler}
                  dropDownMenuClassName="mt-2 shadow-sm" // Added shadow for dropdown menu
                  resetIfChanged
                  meetingLatLng={meetingLatLng}
                  manualAddress={manualAddress}
                  setManualAddress={setManualAddress}
                  setCenterLatLng={setCenterLatLng}
                  setMeetingLatLng={setMeetingLatLng}
                />
              </Form.Item>
            ))}
          {meetingLatLng && !isEditing && !manualAddress && (
            <LocationCard name={"location"} meetingLatLng={meetingLatLng} onEdit={handleEdit} t={t}/>
          )}

          {manualAddress && (
            <LocationInfo
              form={form}
              t={t}
              setManualAddress={setManualAddress}
              setCenterLatLng={setCenterLatLng}
              setMeetingLatLng={setMeetingLatLng}
              selectPlace={selectPlace}
            />
          )}

          {((meetingLatLng && !isEditing) || manualAddress) && (
            <div className="mt-20">
              <Form.Item name="special_instructions" label={t("listings.tabs.location.special_instructions")}>
                <TextArea placeholder={t("listings.tabs.location.special_instructions_placeholder")} rows={4} />
              </Form.Item>
            </div>
          )}
        </Col>
        <Col xs={24} sm={24} md={12}>
          <TripLocation height={450} centerLatLng={centerLatLng} meetingLatLng={meetingLatLng} />
        </Col>
      </Row>
      <ListingActionButtons form={form} onFinish={onFinish} />
    </CustomForm>
  )
}
